import { defineStore } from "pinia";
import Swal from 'sweetalert2';
import { usePosEmployeesStore } from "./pos.employees";
import { useLocalStorage } from "@vueuse/core";
import { deleteDoc, onSnapshot, setDoc, doc, getDoc } from "firebase/firestore";
import { db } from "~/firebaseInstance";
import dayjs from "dayjs";
import axios from "axios";
import { getDevStoreOverride } from "~/utils/devStoreOverride";

// Renew if remaining lifetime falls below this threshold.
const RENEW_THRESHOLD_MS = 3 * 24 * 60 * 60 * 1000; // 3 days
// Breaks the renew → saveSnapshot → onSnapshot → scheduleRenew → renew cycle:
// if the backend hands back a token that is STILL under RENEW_THRESHOLD_MS,
// the snapshot echo would immediately renew again — an unbounded POST
// /users/renew + Firestore write loop that only a long-lived token stopped.
const RENEW_MIN_INTERVAL_MS = 60_000;

const decodeJwtExp = (token: string | null): number | null => {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    const payload = JSON.parse(json);
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
};

const msUntilExpiry = (token: string | null): number | null => {
  const exp = decodeJwtExp(token);
  return exp === null ? null : (exp * 1000) - Date.now();
};
// Define state type (optional, but good practice for complex states)
// interface AuthState {
//   user: object | null;
//   token: string | null;
//   isAuthenticated: boolean;
// }

const LOGIN_URL = "https://app.niceloop.com/api/account/login";
const CHECK_IS_LOGIN_URL = "https://app.niceloop.com/api/account/checkislogin";
const LOGOUT_URL = "https://app.niceloop.com/api/account/logout";

// localStorage key for the offline-boot auth fallback. Whenever we accept a
// fresh auth snapshot (login, Firestore snapshot, renew), we mirror the
// essentials here so the next boot can resolve auth without reaching
// Firestore. Cleared on logout / forceLogout.
const AUTH_CACHE_KEY = "authSnapshot";

type CachedAuthSnapshot = {
  user: any;
  storeId: string | null;
  token: string | null;
  credHash: string | null;
  savedAt: number;
};

function persistAuthSnapshot(snap: CachedAuthSnapshot): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(snap));
  } catch (err) {
    console.warn("[auth] persistAuthSnapshot failed", err);
  }
}

function loadCachedAuthSnapshot(): CachedAuthSnapshot | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.user || !parsed.storeId) return null;
    return parsed as CachedAuthSnapshot;
  } catch (err) {
    console.warn("[auth] loadCachedAuthSnapshot failed", err);
    return null;
  }
}

function clearCachedAuthSnapshot(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(AUTH_CACHE_KEY);
  } catch {}
}

export const useAuthStore = defineStore("auth", {
  state: () => ({
    // storeId : null,
    // isAuthenticated: false,
    browserId: useLocalStorage("browserId", crypto.randomUUID()).value,
    user: null,
    token: null,

    // - for DEV
    storeId: null,
    isAuthenticated: null, // true, false, null

    // Renew/credHash plumbing — underscore-prefixed = internal, not for templates.
    _credHash: null as string | null,
    _renewInFlight: null as Promise<string> | null,
    _renewTimer: null as ReturnType<typeof setTimeout> | null,
    _lastRenewAt: 0,
    _visibilityHandler: null as (() => void) | null,
    _credHashUnsub: null as (() => void) | null,
  }),

  getters: {
    // Example getter:
    // isLoggedIn: (state) => !!state.currentUser,
  },

  actions: {
    async init() {
      // FOR DEV
      if (false && process.env.NODE_ENV === "development") {
        this.storeId = "5422";
        this.user = {
          UserName: "rere.support",
          Locale: "th-TH",
          Row: 0,
          Role: 0,
          Provider: 0,
          AccessList: "pos",
          CustomerId: "5422",
          Id: 13766,
        };
        this.isAuthenticated = true;

        return
      }

      return await this.snapshotAuth();
    },

    // ── Strategy-friendly entrypoints. Bootstrap's OnlineBootStrategy /
    // OfflineBootStrategy call these directly so online/offline auth
    // paths stay separate and readable. `init()`/`snapshotAuth()` above
    // delegate to the same plumbing for callers (e.g. tests, login flow).

    /**
     * Online auth path. Tries the Firestore browser doc with a 5s timeout;
     * on timeout / listener error, falls back to the cached localStorage
     * snapshot when one exists. Throws `need login` only when both server
     * and cache come up empty.
     */
    async hydrateFromFirestore(): Promise<boolean> {
      return await this.snapshotAuth();
    },

    /**
     * Offline auth path. Restores credentials from `localStorage["authSnapshot"]`
     * synchronously — never touches Firestore. Throws `need login` if the
     * cache is missing/invalid. Bootstrap catches that and redirects.
     *
     * Also spawns a background Firestore listener so once connectivity
     * returns, server-side state (token renewal, credHash bumps, deletion)
     * is picked up. This mirrors `snapshotAuth`'s offline fast-path.
     */
    async hydrateFromLocalStorage(): Promise<boolean> {
      if (this.applyDevStoreOverrideSession()) return true;

      const cached = loadCachedAuthSnapshot();
      if (!cached || !cached.user || !cached.storeId) {
        throw new Error("need login");
      }

      this.storeId = cached.storeId;
      this.user = cached.user;
      this.token = cached.token;
      this._credHash = cached.credHash ?? null;
      this.isAuthenticated = true;
      this.startSessionWatchers();
      persistAuthSnapshot({
        user: this.user,
        storeId: this.storeId,
        token: this.token,
        credHash: this._credHash,
        savedAt: Date.now(),
      });

      // Background listener: catch up when network returns. Fire-and-forget.
      try {
        const browserRef = useFirestore().browserRef(this.browserId);
        const unsubscribe = onSnapshot(
          browserRef,
          { includeMetadataChanges: true },
          (snapshot) => {
            if (snapshot.metadata.fromCache) return;
            if (!snapshot.exists()) {
              void this.forceLogout("session_expired");
              unsubscribe();
              return;
            }
            const data = snapshot.data();
            this.storeId = data?.storeId?.toString?.() || null;
            this.user = data?.user;
            this.token = data?.token;
            this._credHash = data?.credHash ?? null;
            this.isAuthenticated = true;
            this.startSessionWatchers();
            persistAuthSnapshot({
              user: this.user,
              storeId: this.storeId,
              token: this.token,
              credHash: this._credHash,
              savedAt: Date.now(),
            });
            void this.verifyCredentialHashOnce();
          },
          (err) => {
            console.warn("[auth] background listener error", err);
          }
        );
      } catch (err) {
        console.warn("[auth] failed to start background listener", err);
      }

      return true;
    },
    async checkIsLogin() {
      try {
        const response = await fetch(CHECK_IS_LOGIN_URL, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
        const data = await response.json();
        this.isAuthenticated = data.isAuthenticated;
      } catch (error) {
        // this.error = error.message;
      } finally {
      }
    },

    // ── DEV-ONLY store impersonation ────────────────────────────────────────
    // When a dev store override is set (via the DEV panel), skip the real auth
    // snapshot entirely and synthesize a full-access session scoped to the
    // chosen shop. No token is needed — REST requests pass the backend's auth +
    // customerId-scope checks via the scope-bypass query pair (see useApi.ts;
    // the pair lives in the dev's localStorage, never in source).
    // Called at the top of BOTH boot auth paths (online snapshotAuth + offline
    // hydrateFromLocalStorage) so it works regardless of connectivity.
    // import.meta.dev gates it out of prod. Returns true when it took over.
    applyDevStoreOverrideSession(): boolean {
      const devOverride = getDevStoreOverride();
      if (!import.meta.dev || !devOverride) return false;
      this.storeId = devOverride;
      this.user = {
        UserName: "dev.override",
        Locale: (this.user as any)?.Locale ?? "en",
        Row: 0,
        Role: 0,
        Provider: 0,
        // Full page-permission set so every report/section is reachable.
        AccessList: "pos,report,menu,expense,stock,setting",
        CustomerId: devOverride,
        Id: 0,
      } as any;
      this.token = null;
      this.isAuthenticated = true;
      console.warn(
        `[auth] DEV store override → impersonating shop ${devOverride} ` +
        `(synthetic session, no token; REST via dev scope-bypass query)`
      );
      return true;
    },

    async snapshotAuth() {
      if (this.applyDevStoreOverrideSession()) return true;

      const browserRef = useFirestore().browserRef(this.browserId);
      // Shorter than v1 (was 10s). With the localStorage fallback below we
      // don't need a long Firestore wait: cached creds boot us instantly
      // and the Firestore listener keeps running in the background to
      // catch up once the server replies.
      const SERVER_WAIT_MS = 5_000;

      const isOffline = () =>
        typeof navigator !== "undefined" && navigator.onLine === false;

      const applySnapshotData = (data: any) => {
        this.storeId = data?.storeId?.toString?.() || null;
        this.user = data?.user;
        this.token = data?.token;
        this._credHash = data?.credHash ?? null;
        this.isAuthenticated = true;
        this.startSessionWatchers();
        persistAuthSnapshot({
          user: this.user,
          storeId: this.storeId,
          token: this.token,
          credHash: this._credHash,
          savedAt: Date.now(),
        });
      };

      // Offline-boot fast path. If localStorage has a usable snapshot, restore
      // it immediately so bootstrap can proceed. A Firestore listener is
      // still started in the background to catch up when connectivity
      // returns (it will overwrite our state and re-persist).
      const cached = loadCachedAuthSnapshot();
      if (cached && (isOffline() || cached.user)) {
        applySnapshotData(cached);
        // Spawn a fire-and-forget Firestore listener so once the network is
        // back we pick up any server-side changes (token renewal, credHash
        // bump). It also runs verifyCredentialHashOnce when a real server
        // snapshot arrives.
        try {
          const unsubscribe = onSnapshot(
            browserRef,
            { includeMetadataChanges: true },
            (snapshot) => {
              if (snapshot.metadata.fromCache) return;
              if (!snapshot.exists()) {
                // Server says no doc → force logout to /login.
                void this.forceLogout('session_expired');
                unsubscribe();
                return;
              }
              applySnapshotData(snapshot.data());
              void this.verifyCredentialHashOnce();
            },
            (err) => {
              console.warn("[auth] background snapshot listener error", err);
            }
          );
        } catch (err) {
          console.warn("[auth] failed to start background listener", err);
        }
        return true;
      }

      return new Promise((resolve, reject) => {
        let settled = false;
        let lastCachedSnap: any = null;
        let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
        let unsubscribe: (() => void) | null = null;

        const acceptSnapshot = (snapshot: any, fromCache: boolean) => {
          if (settled) return;
          settled = true;
          if (timeoutHandle) clearTimeout(timeoutHandle);
          unsubscribe?.();

          if (!snapshot.exists()) {
            this.storeId = null;
            this.user = null;
            this.token = null;
            this.isAuthenticated = false;
            clearCachedAuthSnapshot();
            reject(new Error("need login"));
            return;
          }

          applySnapshotData(snapshot.data());
          if (!fromCache) void this.verifyCredentialHashOnce();
          resolve(true);
        };

        unsubscribe = onSnapshot(
          browserRef,
          { includeMetadataChanges: true },
          (snapshot) => {
            if (settled) return;

            if (snapshot.metadata.fromCache) {
              // Hold the cached snapshot. If we're already offline, use it
              // immediately — server snapshot will never arrive. If online,
              // hold it as a fallback for the timeout below.
              lastCachedSnap = snapshot;
              if (isOffline()) acceptSnapshot(snapshot, true);
              return;
            }

            // Server-confirmed snapshot — preferred path.
            acceptSnapshot(snapshot, false);
          },
          (error) => {
            if (settled) return;
            // If Firestore listener errors out but we already saw a cached
            // snapshot with credentials, fall back to it instead of forcing
            // the user back to /login.
            if (lastCachedSnap && lastCachedSnap.exists()) {
              console.warn("[auth] firestore listener error, using cached snapshot", error);
              acceptSnapshot(lastCachedSnap, true);
              return;
            }
            settled = true;
            if (timeoutHandle) clearTimeout(timeoutHandle);
            unsubscribe?.();
            this.storeId = null;
            this.user = null;
            this.token = null;
            this.isAuthenticated = false;
            reject(error instanceof Error ? error : new Error(String(error || "auth snapshot failed")));
          }
        );

        // Online-but-slow safety net: if no server snapshot arrives within
        // SERVER_WAIT_MS and we have a cached doc, accept the cache so boot
        // can proceed. Without this, a flaky/slow connection would hang the
        // loading screen indefinitely.
        timeoutHandle = setTimeout(() => {
          if (settled) return;
          if (lastCachedSnap && lastCachedSnap.exists()) {
            console.warn("[auth] server snapshot timed out, using cached snapshot");
            acceptSnapshot(lastCachedSnap, true);
          }
        }, SERVER_WAIT_MS);
      });
    },

    async watchFirestoreOffline(onChange) {
      const browserRef = useFirestore().browserRef(this.browserId);
      let last = null;

      return onSnapshot(
        browserRef,
        { includeMetadataChanges: true },
        (snap) => {
          if (!snap.exists()) return;

          const now = snap.metadata.fromCache === true;
          // แจ้งเฉพาะตอนสถานะเปลี่ยนจริง ๆ
          if (last !== now) {
            onChange({
              isFirestoreOffline: now,
              source: now ? "cache" : "server",
            });
            last = now;
          }
        },
        (err) => {
          // error จริง (เช่น permission) ไม่ใช่สลับ offline เฉย ๆ
          console.warn("Firestore listener error:", err);
        }
      );
    },

    async saveSnapshot() {
      const browserRef = useFirestore().browserRef(this.browserId);
      return setDoc(browserRef, {
        user: this.user,
        storeId: this.storeId,
        timestamp: dayjs().toISOString(),
        token: this.token,
        credHash: this._credHash ?? null,
      });
    },




    async login(credentials: any, forceLoadSqlResources = false) {

      const {
        public: { apiBase },
      } = useRuntimeConfig();



      // let _data = JSON.stringify({
      //   "UserName": "rice",
      //   "Password": "123456"
      // });


      let _data = JSON.stringify(credentials);
      let config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: apiBase + '/users/login',
        headers: {
          'Content-Type': 'application/json'
        },
        data: _data
      };




      const response = await axios.request(config)

      await this.applySession(response.data);
      await Swal.fire({ icon: 'success', title: "Login Success", confirmButtonText: "OK", showCancelButton: false })

      /*if (forceLoadSqlResources) {
        await this.initClientDatabaseService()
      }*/

      return response;

    },

    // Turn a /users/login or /account/register body into a live session. Both
    // endpoints answer with the same shape on purpose, so a shop that just
    // signed up lands in exactly the state a sign-in produces.
    async applySession(data: any) {
      this.user = data.user;
      this.storeId = data.CustomerId.toString();
      this.token = data.token;
      this._credHash = data.credHash ?? null;
      localStorage.setItem("token", data.token);
      await this.saveSnapshot();
      persistAuthSnapshot({
        user: this.user,
        storeId: this.storeId,
        token: this.token,
        credHash: this._credHash,
        savedAt: Date.now(),
      });

      useFirestore().setStoreId(this.storeId);
      usePosShopStore().storeId = this.storeId

      this.isAuthenticated = true;
      this.startSessionWatchers();
    },

    // Sign-up. Public endpoint: no token and no customerId to send, which is why
    // it goes through axios rather than useApi/useDataservice (those inject both).
    async register(payload: any) {
      const {
        public: { apiBase },
      } = useRuntimeConfig();

      const response = await axios.request({
        method: 'post',
        maxBodyLength: Infinity,
        url: apiBase + '/account/register',
        headers: { 'Content-Type': 'application/json' },
        data: JSON.stringify(payload),
      });

      await this.applySession(response.data);
      return response;
    },
    async logout() {
      try {
        // await useCashierLock().leaveCashierMode(null);
        this.stopSessionWatchers();
        // Drop the cashier machine's RTDB custom-token session;
        // the next login will mint a fresh one via /users/firebase-token.
        await signOutCashierFirebaseAuth();

        const browserRef = useFirestore().browserRef(this.browserId);
        const r = await deleteDoc(browserRef);

        this.user = null;
        this._credHash = null;
        localStorage.removeItem("token");
        localStorage.removeItem("firestore_loaded")
        clearCachedAuthSnapshot();

        // Shops share devices and staff sign in and out of several accounts.
        // The stock cache is keyed per customer so it can never be read by the
        // wrong shop, but do not leave one shop's whole transaction history on
        // disk for the next account. Never let this block the sign-out.
        try {
          const { resetAllStockTransactions } = await import("~/stock/useStockTransactions");
          await resetAllStockTransactions();
        } catch (err) {
          console.error("[auth] stock cache reset failed", err);
        }

        this.isAuthenticated = null;

        alert('logout success')
        window.location.href = '/'
        // return r
        // usePosEmployeesStore().lockScreen();
      } catch (error) {
        // this.error = error.message;
      } finally {
      }
    },

    // Renew the access JWT before it expires. Idempotent — concurrent callers
    // collapse onto a single in-flight request via _renewInFlight.
    async renew(): Promise<string | null> {
      if (this._renewInFlight) return this._renewInFlight;
      if (!this.token) return null;
      const { public: { apiBase } } = useRuntimeConfig();
      const tokenAtStart = this.token;

      this._renewInFlight = (async () => {
        try {
          const res = await axios.request({
            method: 'post',
            url: apiBase + '/users/renew',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${tokenAtStart}`,
            },
            data: {},
          });
          const newToken = res?.data?.token;
          if (!newToken) throw new Error('renew: missing token in response');
          this._lastRenewAt = Date.now();
          this.token = newToken;
          localStorage.setItem('token', newToken);
          await this.saveSnapshot();
          persistAuthSnapshot({
            user: this.user,
            storeId: this.storeId,
            token: this.token,
            credHash: this._credHash,
            savedAt: Date.now(),
          });
          this.scheduleRenew();
          return newToken;
        } finally {
          this._renewInFlight = null;
        }
      })();

      try {
        return await this._renewInFlight;
      } catch (err) {
        console.warn('[auth] renew failed', err);
        return null;
      }
    },

    // Fire renew if remaining lifetime is under the threshold. Safe to call
    // any time (boot, visibilitychange, timer); becomes a no-op when not needed.
    maybeRenewIfBelowThreshold() {
      const remaining = msUntilExpiry(this.token);
      if (remaining === null) return; // pre-JWT legacy token: ignore
      if (remaining <= 0) return;     // already expired: 401 path will redirect
      // A renew just succeeded — if the fresh token is still under threshold,
      // renewing again won't get a longer one. Wait out the interval instead
      // of looping through the snapshot echo.
      if (Date.now() - this._lastRenewAt < RENEW_MIN_INTERVAL_MS) return;
      if (remaining < RENEW_THRESHOLD_MS) {
        void this.renew();
      }
    },

    // Schedule a single timer that fires exactly when 3 days remain.
    scheduleRenew() {
      if (this._renewTimer) {
        clearTimeout(this._renewTimer);
        this._renewTimer = null;
      }
      const remaining = msUntilExpiry(this.token);
      if (remaining === null || remaining <= 0) return;
      if (remaining < RENEW_THRESHOLD_MS) {
        // Already under threshold — renew now; success path reschedules.
        this.maybeRenewIfBelowThreshold();
        return;
      }
      this._renewTimer = setTimeout(() => {
        this._renewTimer = null;
        this.maybeRenewIfBelowThreshold();
      }, remaining - RENEW_THRESHOLD_MS);
    },

    // Re-check the threshold whenever the tab becomes visible — covers the
    // case where the timer slept past its fire time (laptop closed for hours).
    installVisibilityWatcher() {
      if (!import.meta.client) return;
      if (this._visibilityHandler) return;
      this._visibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          this.maybeRenewIfBelowThreshold();
        }
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
    },

    removeVisibilityWatcher() {
      if (!import.meta.client) return;
      if (!this._visibilityHandler) return;
      document.removeEventListener('visibilitychange', this._visibilityHandler);
      this._visibilityHandler = null;
    },

    // Subscribe to users-auth/{userId}.credHash. When the server bumps the hash
    // (because the user changed their password elsewhere), the snapshot fires
    // and we force-logout instantly.
    watchCredentialHash(userId: number) {
      if (!import.meta.client) return;
      if (this._credHashUnsub) return;
      if (!userId || !Number.isFinite(userId)) return;

      const ref = doc(db, 'users-auth', String(userId));

      // Backfill baseline if missing (e.g., a session restored before the
      // login response carried credHash). One-shot read; the listener below
      // handles all subsequent changes.
      if (!this._credHash) {
        getDoc(ref).then((snap) => {
          if (snap.exists()) {
            const v = snap.data().credHash;
            if (typeof v === 'string') {
              this._credHash = v;
              void this.saveSnapshot();
            }
          }
        }).catch(() => {});
      }

      this._credHashUnsub = onSnapshot(
        ref,
        { includeMetadataChanges: true },
        (snap) => {
          if (snap.metadata.fromCache) return;
          if (!snap.exists()) return;
          const incoming = snap.data().credHash;
          if (typeof incoming !== 'string') return;
          if (!this._credHash) {
            // First server-confirmed value — adopt as baseline.
            this._credHash = incoming;
            void this.saveSnapshot();
            return;
          }
          if (incoming !== this._credHash) {
            void this.forceLogout('password_changed');
          }
        },
        (err) => {
          console.warn('[auth] credHash watcher error', err);
        },
      );
    },

    // One-shot check (page load only). If the server's credHash differs from
    // the one stored at last login, the password was changed elsewhere — force
    // re-login. Does nothing if local hash is missing (legacy session) or if
    // the server doc is absent (network/permissions issue — fail open).
    async verifyCredentialHashOnce() {
      if (!import.meta.client) return;
      const userId = Number((this.user as any)?.Id);
      if (!Number.isFinite(userId) || !this._credHash) return;
      try {
        const snap = await getDoc(doc(db, 'users-auth', String(userId)));
        if (!snap.exists()) return;
        const incoming = snap.data().credHash;
        if (typeof incoming === 'string' && incoming !== this._credHash) {
          void this.forceLogout('password_changed');
        }
      } catch (err) {
        console.warn('[auth] verifyCredentialHashOnce failed', err);
      }
    },

    // Pull the fresh current-user record and merge its profile fields over the
    // login-time snapshot. The Firestore browser doc + localStorage snapshot are
    // written at login and never refreshed by `renew` (token only) or the credHash
    // path (password only) — so without this, an admin's Role / Locale / AccessList
    // edit wouldn't reach an already-logged-in device until it logged out and back
    // in. Online-only, fail-open: any error keeps the cached user untouched.
    //
    // Fired background (non-blocking) from the online boot strategy, so a changed
    // AccessList is enforced by the route guard on the next navigation after boot.
    async refreshCurrentUserProfile(): Promise<void> {
      if (!import.meta.client) return;
      // DEV-ONLY store override: the synthetic session has no real user/token, so
      // /users/current-user 401s under the dev scope-bypass query (it reads req.auth
      // internally). The useApi 401 handler then hard-redirects to /login. Skip it —
      // there's no real profile to refresh when impersonating.
      if (import.meta.dev && getDevStoreOverride()) return;
      if (!this.user) return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      try {
        const res: any = await useEntity("users", "/users/current-user")
          .fetch({ customerId: this.storeId });
        const fresh = Array.isArray(res) ? res[0] : res;
        if (!fresh || typeof fresh !== "object") return;

        const prev = this.user as any;
        // Defensive: ignore a record for a different user (mismatched/stale token).
        if (fresh.Id != null && prev?.Id != null && String(fresh.Id) !== String(prev.Id)) {
          return;
        }

        const changed =
          prev.Role !== fresh.Role ||
          prev.Locale !== fresh.Locale ||
          prev.AccessList !== fresh.AccessList;

        // Merge so any field current-user omits is preserved from the login record.
        this.user = { ...prev, ...fresh };

        if (changed) {
          await this.saveSnapshot();
          persistAuthSnapshot({
            user: this.user,
            storeId: this.storeId,
            token: this.token,
            credHash: this._credHash,
            savedAt: Date.now(),
          });
        }
      } catch (err) {
        console.warn("[auth] refreshCurrentUserProfile failed", err);
      }
    },

    // Wraps the boilerplate of "start everything needed once we're authenticated".
    startSessionWatchers() {
      if (!import.meta.client) return;
      this.scheduleRenew();
      this.installVisibilityWatcher();
      // NOTE: credHash watcher intentionally NOT started. Many POS deployments
      // share one username/password across multiple cashier terminals — if we
      // forced logout on credHash change, a password change would kick every
      // cashier out mid-sale. The renew flow above keeps sessions alive;
      // password rotation is enforced lazily (next natural re-login).
      // To re-enable per-session revocation, uncomment the lines below — but
      // only do so once accounts are no longer shared.
      // const userId = Number((this.user as any)?.Id);
      // if (Number.isFinite(userId)) this.watchCredentialHash(userId);
    },

    stopSessionWatchers() {
      if (this._renewTimer) {
        clearTimeout(this._renewTimer);
        this._renewTimer = null;
      }
      this._renewInFlight = null;
      this.removeVisibilityWatcher();
      if (this._credHashUnsub) {
        this._credHashUnsub();
        this._credHashUnsub = null;
      }
    },

    // Server-detected revocation (e.g., password changed elsewhere). Clean up
    // local state and bounce to /login with a reason in the query string.
    async forceLogout(reason: string) {
      this.stopSessionWatchers();
      await signOutCashierFirebaseAuth();
      try {
        const browserRef = useFirestore().browserRef(this.browserId);
        await deleteDoc(browserRef);
      } catch {}
      this.user = null;
      this.token = null;
      this._credHash = null;
      this.isAuthenticated = false;
      localStorage.removeItem('token');
      localStorage.removeItem('firestore_loaded');
      clearCachedAuthSnapshot();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = `/login?reason=${encodeURIComponent(reason)}`;
      }
    },
  },
});

// {
//   "CustomerInfo": {
//       "CreateDate": "2018-11-01T05:08:51.47",
//       "ExpireDate": "2025-11-09T00:00:00",
//       "PackagePlan": "1390",
//       "IsTrial": false,
//       "Name": "okaki@niceloop.com",
//       "Id": 5467
//   },
//   "UserInfo": {
//       "UserName": "okaki01",
//       "Locale": "th-TH",
//       "Row": 0,
//       "Role": 1,
//       "Provider": 0,
//       "AccessList": "pos",
//       "CustomerId": 5467,
//       "Id": 13421
//   },
//   "SystemInfo": [
//       {
//           "Value": "Omi Beef OKAKI",
//           "Name": "ShopName",
//           "CustomerId": 5467,
//           "Id": 21103
//       },
//       {
//           "Value": "กรุงเทพมหานคร",
//           "Name": "province",
//           "CustomerId": 5467,
//           "Id": 21104
//       },
//       {
//           "Value": "100",
//           "Name": "Table",
//           "CustomerId": 5467,
//           "Id": 21105
//       },
//       {
//           "Value": "0655188114",
//           "Name": "SmsReport",
//           "CustomerId": 5467,
//           "Id": 21106
//       },
//       {
//           "Value": "okaki@niceloop.com",
//           "Name": "EmailReport",
//           "CustomerId": 5467,
//           "Id": 21107
//       },
//       {
//           "Value": "true",
//           "Name": "IsLockEnable",
//           "CustomerId": 5467,
//           "Id": 21108
//       },
//       {
//           "Value": "true",
//           "Name": "VoidOnMobile",
//           "CustomerId": 5467,
//           "Id": 21109
//       },
//       {
//           "Value": "true",
//           "Name": "CustomerMode",
//           "CustomerId": 5467,
//           "Id": 21110
//       },
//       {
//           "Value": "true",
//           "Name": "UseJob",
//           "CustomerId": 5467,
//           "Id": 21111
//       },
//       {
//           "Value": "true",
//           "Name": "PrintMoveTable",
//           "CustomerId": 5467,
//           "Id": 21112
//       },
//       {
//           "Value": "7",
//           "Name": "TaxRate1",
//           "CustomerId": 5467,
//           "Id": 21120
//       },
//       {
//           "Value": "2",
//           "Name": "TaxMode",
//           "CustomerId": 5467,
//           "Id": 21121
//       },
//       {
//           "Value": "1",
//           "Name": "TaxStatus",
//           "CustomerId": 5467,
//           "Id": 21122
//       },
//       {
//           "Value": "8",
//           "Name": "ServiceCharge",
//           "CustomerId": 5467,
//           "Id": 21123
//       },
//       {
//           "Value": "0105561090924",
//           "Name": "TaxId",
//           "CustomerId": 5467,
//           "Id": 21124
//       },
//       {
//           "Value": "1",
//           "Name": "BusinessMode",
//           "CustomerId": 5467,
//           "Id": 21125
//       },
//       {
//           "Value": "0",
//           "Name": "NoRoundUp",
//           "CustomerId": 5467,
//           "Id": 40391
//       }
//   ]
// }
